import type {ResourceConfig} from '@github-ui/custom-properties-types'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {Banner, Link} from '@primer/react'

type Props = {
  propertyName: string
  resourceUsages?: string[]
  resourceConfig?: Record<string, ResourceConfig>
  urlPrefix?: string
}

export function DefinitionUsagesBanner({propertyName, resourceUsages, resourceConfig, urlPrefix}: Props) {
  const showResourceUsages = isFeatureEnabled('custom_property_usages_ui')

  // Only include usages that have a known display name — we don't want to surface resource class names to users
  const displayableUsages = resourceUsages?.flatMap(usage => {
    const config = resourceConfig?.[usage]
    if (!config) return []

    const rawPath = urlPrefix ? config.resourcePath?.(urlPrefix) : undefined
    const absolutePath = rawPath && !rawPath.startsWith('/') ? `/${rawPath}` : rawPath

    let path: string | undefined
    if (absolutePath) {
      const url = new URL(absolutePath, window.location.origin)
      url.searchParams.set('custom-property-usages', 'true')
      path = `${url.pathname}${url.search}${url.hash}`
    }

    return [{resourceType: usage, displayName: config.displayName, path}]
  })
  const hasUsages = showResourceUsages && displayableUsages && displayableUsages.length > 0

  const renderDescription = () => {
    if (!showResourceUsages) {
      return (
        <span>
          The <strong>{propertyName}</strong> property may be in use by other GitHub features. Please verify before
          deletion.
        </span>
      )
    }

    return (
      <>
        {hasUsages ? (
          <span>
            The <strong>{propertyName}</strong> property may be in use by the following GitHub features:{' '}
            {displayableUsages?.map((usage, index) => (
              <span key={usage.resourceType}>
                {index > 0 && <span>, </span>}
                {usage.path ? (
                  <Link href={usage.path} target="_blank" rel="noopener noreferrer" inline>
                    <strong>{usage.displayName}</strong>
                  </Link>
                ) : (
                  <strong>{usage.displayName}</strong>
                )}
              </span>
            ))}
            .
          </span>
        ) : (
          <span>
            No other GitHub features found that use the property: <strong>{propertyName}</strong>.
          </span>
        )}
      </>
    )
  }

  return (
    <Banner
      title="Other definition usages"
      hideTitle
      variant={hasUsages || !showResourceUsages ? 'warning' : 'info'}
      data-testid="usages-banner"
      description={renderDescription()}
    />
  )
}
